import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";
import * as lucide from "lucide";

import { imageBase } from "../../api/client";
import type {
	AgentInbox,
	AgentLinkSummary,
	AgentsData,
	PendingSend,
	ReachableAgent,
	SentItem,
	SharedContext,
	WaitingItem,
	WaitingKind,
} from "../../api/schemas/agents";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import { durationText, relativeTime, retentionChip } from "../../widgets/a2aUi";
import { confirmDialog } from "../../widgets/dialog";
import { Loader2, icon } from "../../widgets/icons";
import { agentLabelText } from "../../widgets/provenance";
import * as Picker from "../../widgets/recordPicker";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";
import { shareAgentId } from "./update";

type H = HtmlBuilder<Message>;
type Name = (id: string) => string;

/**
 * Agents by key name, the signed-in user by name, other humans (Clerk ids)
 * by a generic label: no member directory yet, and raw ids never render.
 */
const namer =
	(data: AgentsData, shared: Shared): Name =>
	(id) => {
		const key = data.keys.find((k) => k.id === id);
		if (key) return key.description ?? agentLabelText(null);
		if (id === shared.user.id) return shared.user.name;
		// A2A filed by a link holder: sender is 'link:<linkId>' (server a2aRoutes).
		if (id.startsWith("link:")) {
			const link = data.links.find((l) => l.id === id.slice(5));
			return link
				? i18next.t("agents.viaLink", { label: link.label })
				: i18next.t("agents.unknownLink");
		}
		return id.startsWith("user_")
			? i18next.t("agents.someone")
			: agentLabelText(null);
	};

const route = (name: Name, from: string, to: readonly string[]) =>
	i18next.t("agents.route", {
		from: name(from),
		to: to.map(name).join(", "),
	});

const absoluteTime = (iso: string): string =>
	new Intl.DateTimeFormat(i18next.language, {
		weekday: "short",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	}).format(Date.parse(iso));

const timeEl = (h: H, iso: string, text: string, className = ""): Html =>
	h.time(
		[
			h.Attribute("datetime", iso),
			h.Title(absoluteTime(iso)),
			h.Class(className),
		],
		[text],
	);

const KIND: Record<
	WaitingKind,
	{ icon: keyof typeof lucide; label: () => string }
> = {
	message: {
		icon: "MessageSquare",
		label: () => i18next.t("agents.kind.message"),
	},
	context: { icon: "Layers", label: () => i18next.t("agents.kind.context") },
	task: { icon: "ListTodo", label: () => i18next.t("agents.kind.task") },
};

const kindChip = (h: H, kind: WaitingKind): Html =>
	h.span(
		[
			h.DataAttribute("testid", `kind-${kind}`),
			h.Class("badge badge-sm badge-ghost gap-1 shrink-0"),
		],
		[
			icon(h, KIND[kind].icon, lucide[KIND[kind].icon] as typeof lucide.Bot, {
				size: 12,
			}),
			KIND[kind].label(),
		],
	);

const card = (h: H, testId: string, heading: string, body: Html[]): Html =>
	h.section(
		[
			h.DataAttribute("testid", testId),
			h.AriaLabel(heading),
			h.Class("card card-border bg-base-100 p-4 flex flex-col gap-3 min-w-0"),
		],
		[h.h2([h.Class("text-base font-semibold")], [heading]), ...body],
	);

const muted = (h: H, text: string): Html =>
	h.p([h.Class("text-sm text-base-content/70")], [text]);

// ---- Expires next ---------------------------------------------------------

const expiresNext = (h: H, data: AgentsData, name: Name): Html => {
	const next = data.expiresNext;
	return h.section(
		[
			h.DataAttribute("testid", "expires-next"),
			h.AriaLabel(i18next.t("agents.expiresNext.heading")),
			h.Class(
				"card card-border border-warning/50 bg-warning/10 px-4 py-3 flex flex-row flex-wrap items-center gap-x-6 gap-y-1",
			),
		],
		[
			h.div(
				[
					h.Class(
						"flex items-center gap-2 text-sm font-semibold uppercase tracking-wide",
					),
				],
				[
					icon(h, "Hourglass", lucide.Hourglass, {
						size: 18,
						class: "text-warning",
					}),
					i18next.t("agents.expiresNext.heading"),
				],
			),
			...(next?.expiresAt
				? [
						h.div(
							[h.Class("flex flex-col min-w-0")],
							[
								h.span(
									[
										h.DataAttribute("testid", "expires-next-title"),
										h.Class("text-lg font-semibold truncate"),
									],
									[next.title],
								),
								h.span(
									[h.Class("text-sm text-base-content/80")],
									[route(name, next.sender, next.recipients)],
								),
							],
						),
						h.div(
							[h.Class("ml-auto flex items-baseline gap-3")],
							[
								h.span(
									[
										h.DataAttribute("testid", "expires-next-countdown"),
										h.Class("text-2xl font-bold tabular-nums"),
									],
									[
										i18next.t("agents.expiresNext.countdown", {
											duration: durationText(
												Date.parse(next.expiresAt) - data.now,
											),
										}),
									],
								),
								timeEl(
									h,
									next.expiresAt,
									absoluteTime(next.expiresAt),
									"text-sm text-base-content/80",
								),
							],
						),
					]
				: [muted(h, i18next.t("agents.expiresNext.none"))]),
		],
	);
};

// ---- Waiting --------------------------------------------------------------

const waitingItem = (h: H, item: WaitingItem, name: Name, now: number) =>
	h.li(
		[h.Class("flex items-start gap-2 text-sm")],
		[
			kindChip(h, item.kind),
			h.div(
				[h.Class("min-w-0 flex-1")],
				[
					h.p([h.Class("truncate font-medium")], [item.title || "—"]),
					h.p(
						[h.Class("text-xs text-base-content/70")],
						[
							name(item.from),
							" · ",
							timeEl(h, item.sentAt, relativeTime(item.sentAt, now)),
						],
					),
				],
			),
		],
	);

const agentInbox = (h: H, inbox: AgentInbox, name: Name, now: number) => {
	const agent = name(inbox.recipient);
	return h.li(
		[
			h.Key(inbox.recipient),
			h.DataAttribute("testid", "agent-inbox"),
			h.Class(
				"flex flex-col gap-1.5 border-b border-base-200 pb-2 last:border-0",
			),
		],
		[
			h.div(
				[h.Class("flex items-center gap-2")],
				[
					icon(h, "Bot", lucide.Bot, { size: 16, class: "text-secondary" }),
					h.h3([h.Class("font-semibold truncate flex-1")], [agent]),
					h.span(
						[
							h.Role("img"),
							h.AriaLabel(
								i18next.t("agents.waiting.countLabel", {
									name: agent,
									count: inbox.pending,
								}),
							),
							h.DataAttribute("testid", "pending-count"),
							h.Class(
								`badge tabular-nums font-bold ${inbox.pending > 0 ? "badge-primary" : "badge-ghost"}`,
							),
						],
						[String(inbox.pending)],
					),
				],
			),
			inbox.items.length === 0
				? h.p(
						[h.Class("text-xs text-base-content/60 pl-6")],
						[i18next.t("agents.waiting.empty")],
					)
				: h.ul(
						[h.Class("flex flex-col gap-1.5 pl-6")],
						inbox.items.map((item) => waitingItem(h, item, name, now)),
					),
		],
	);
};

const waiting = (h: H, data: AgentsData, name: Name) =>
	card(h, "waiting", i18next.t("agents.waiting.heading"), [
		data.inbox.length === 0
			? muted(h, i18next.t("agents.waiting.noAgents"))
			: h.ul(
					[h.Class("flex flex-col gap-2")],
					data.inbox.map((inbox) => agentInbox(h, inbox, name, data.now)),
				),
	]);

// ---- Shared contexts ------------------------------------------------------

const promotedCell = (h: H, shared: Shared, context: SharedContext): Html => {
	const promoted = context.promoted;
	if (promoted === null)
		return h.span(
			[h.Class("text-xs text-base-content/60 whitespace-nowrap")],
			[i18next.t("agents.contexts.notPromoted")],
		);
	const target =
		promoted.target === "task"
			? i18next.t("agents.contexts.promotedTask")
			: i18next.t("agents.contexts.promotedEntry");
	const href = linkHref(
		shared.route,
		promoted.target === "task"
			? Route.Task({ id: promoted.id, asOf: Option.none() })
			: Route.Entry({ id: promoted.id, asOf: Option.none() }),
	);
	return h.a(
		[
			h.Href(href),
			h.DataAttribute("testid", "promoted-badge"),
			h.AriaLabel(i18next.t("agents.contexts.promotedLabel", { target })),
			h.Class(
				"badge badge-sm badge-success badge-soft text-base-content gap-1",
			),
		],
		[
			icon(h, "ArrowUpRight", lucide.ArrowUpRight, {
				size: 12,
				class: "text-success",
			}),
			target,
		],
	);
};

const actions = (h: H, context: SharedContext): Html =>
	h.div(
		[h.Class("flex shrink-0 justify-end gap-1")],
		[
			context.promoted === null
				? h.button(
						[
							h.Type("button"),
							h.Class("btn btn-xs btn-ghost"),
							h.AriaLabel(
								i18next.t("agents.contexts.promoteLabel", {
									title: context.title,
								}),
							),
							h.OnClick(
								Message.ClickedPromote({
									contextId: context.contextId,
									title: context.title,
								}),
							),
						],
						[i18next.t("agents.contexts.promote")],
					)
				: h.empty,
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-xs btn-ghost text-error"),
					h.AriaLabel(
						i18next.t("agents.contexts.revokeLabel", { title: context.title }),
					),
					h.OnClick(
						Message.ClickedRevoke({
							contextId: context.contextId,
							title: context.title,
						}),
					),
				],
				[i18next.t("agents.contexts.revoke")],
			),
		],
	);

const contexts = (h: H, data: AgentsData, shared: Shared, name: Name) => {
	const live = Option.isNone(shared.asOf);
	const headers = [
		i18next.t("agents.contexts.title"),
		i18next.t("agents.contexts.from"),
		i18next.t("agents.contexts.to"),
		i18next.t("agents.contexts.retention"),
		i18next.t("agents.contexts.promoted"),
		...(live ? [i18next.t("agents.contexts.actions")] : []),
	];
	return card(h, "shared-contexts", i18next.t("agents.contexts.heading"), [
		data.contexts.length === 0
			? muted(h, i18next.t("agents.contexts.empty"))
			: h.div(
					[h.Class("overflow-x-auto")],
					[
						h.table(
							[h.Class("table table-sm")],
							[
								h.thead(
									[],
									[
										h.tr(
											[],
											headers.map((header) => h.th([], [header])),
										),
									],
								),
								h.tbody(
									[],
									data.contexts.map((context) =>
										h.tr(
											[h.Key(context.contextId)],
											[
												h.td(
													[h.Class("min-w-48 font-medium")],
													[context.title || "—"],
												),
												h.td([], [name(context.sender)]),
												h.td([], [context.recipients.map(name).join(", ")]),
												h.td(
													[],
													[
														retentionChip(h, context.expiresAt, data.now),
														// The absolute expiry rides under the chip; a separate column pushed Revoke off the card.
														context.expiresAt === null
															? h.empty
															: h.div(
																	[
																		h.Class(
																			"mt-1 whitespace-nowrap text-xs text-base-content/60",
																		),
																	],
																	[
																		timeEl(
																			h,
																			context.expiresAt,
																			absoluteTime(context.expiresAt),
																		),
																	],
																),
													],
												),
												h.td([], [promotedCell(h, shared, context)]),
												...(live
													? [
															h.td(
																[h.Class("whitespace-nowrap")],
																[actions(h, context)],
															),
														]
													: []),
											],
										),
									),
								),
							],
						),
					],
				),
	]);
};

// ---- Sent by agents -------------------------------------------------------

const sentRow = (h: H, item: SentItem, name: Name, now: number) =>
	h.li(
		[
			h.Key(`${item.recipient}:${item.seq}`),
			// Wraps to a second line on a phone instead of scrolling the page.
			h.Class("flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm min-w-0"),
		],
		[
			kindChip(h, item.kind),
			h.span(
				[
					h.Class(
						`min-w-0 flex-1 basis-32 truncate ${item.gone ? "italic text-base-content/60 line-through" : "font-medium"}`,
					),
				],
				[item.title || "—"],
			),
			item.gone
				? h.span(
						[h.Class("text-xs italic text-base-content/60 whitespace-nowrap")],
						[i18next.t("agents.sent.gone")],
					)
				: h.empty,
			h.span(
				[
					h.Class(
						"ml-auto flex min-w-0 max-w-full items-center gap-2 text-xs text-base-content/70",
					),
				],
				[
					h.span(
						[h.Class("min-w-0 truncate")],
						[route(name, item.from, [item.recipient])],
					),
					h.span(
						[h.Class("whitespace-nowrap")],
						[timeEl(h, item.sentAt, relativeTime(item.sentAt, now))],
					),
					h.span(
						[h.Class("badge badge-xs badge-ghost whitespace-nowrap")],
						[i18next.t(item.acked ? "agents.sent.read" : "agents.sent.unread")],
					),
				],
			),
		],
	);

const sent = (h: H, data: AgentsData, name: Name) =>
	card(h, "sent-feed", i18next.t("agents.sent.heading"), [
		data.sent.length === 0
			? muted(h, i18next.t("agents.sent.empty"))
			: h.ul(
					[h.Class("flex flex-col gap-1.5")],
					data.sent.map((item) => sentRow(h, item, name, data.now)),
				),
	]);

// ---- Share an agent -------------------------------------------------------

const recordHref = (shared: Shared, id: string) =>
	linkHref(shared.route, Route.Record({ id, asOf: Option.none() }));

const smallButton = (
	h: H,
	text: string,
	ariaLabel: string,
	onClick: Message,
	className = "btn-ghost",
	disabled = false,
): Html =>
	h.button(
		[
			h.Type("button"),
			h.Class(`btn btn-xs ${className}`),
			h.AriaLabel(ariaLabel),
			h.Disabled(disabled),
			h.OnClick(onClick),
		],
		[text],
	);

/** What a holder's agent sees at the link: a fixed anonymous card, never the owner's label. */
const sharePreview = (h: H): Html =>
	h.div(
		[
			h.DataAttribute("testid", "share-preview"),
			h.Class("rounded-box border border-base-300 bg-base-200/50 p-3 text-sm"),
		],
		[
			h.p(
				[h.Class("text-xs uppercase tracking-wide text-base-content/60")],
				[i18next.t("agents.share.previewHeading")],
			),
			h.p(
				[h.Class("flex items-center gap-2 font-semibold")],
				[
					icon(h, "Bot", lucide.Bot, { size: 16, class: "text-secondary" }),
					i18next.t("agents.share.previewName"),
				],
			),
			h.p(
				[h.Class("text-xs text-base-content/70")],
				[i18next.t("agents.share.previewNote")],
			),
		],
	);

const shareForm = (h: H, model: Model, data: AgentsData): Html => {
	const s = model.share;
	const agentId = shareAgentId(model);
	return h.form(
		[
			h.Id("share-form"),
			h.Class("flex flex-col gap-3"),
			h.OnSubmit(Message.SubmittedShare()),
		],
		[
			h.div(
				[h.Class("grid gap-3 sm:grid-cols-2")],
				[
					h.div(
						[h.Class("flex flex-col gap-1 text-sm")],
						[
							h.label(
								[h.For("share-agent")],
								[i18next.t("agents.share.agentLabel")],
							),
							h.select(
								[
									h.Id("share-agent"),
									h.Class("select select-sm w-full"),
									h.Value(agentId),
									h.OnChange((value) => Message.ChangedShareAgent({ value })),
								],
								data.keys.map((k) =>
									h.option(
										[h.Value(k.id), h.Selected(k.id === agentId)],
										[k.description ?? agentLabelText(null)],
									),
								),
							),
						],
					),
					h.div(
						[h.Class("flex flex-col gap-1 text-sm")],
						[
							h.label(
								[h.For("share-label")],
								[i18next.t("agents.share.forLabel")],
							),
							h.input([
								h.Id("share-label"),
								h.Attribute("aria-describedby", "share-label-hint"),
								h.Class("input input-sm w-full"),
								h.Placeholder(i18next.t("agents.share.forPlaceholder")),
								h.Value(s.label),
								h.OnInput((value) => Message.ChangedShareLabel({ value })),
							]),
							h.span(
								[
									h.Id("share-label-hint"),
									h.Class("text-xs text-base-content/60"),
								],
								[i18next.t("agents.share.forHint")],
							),
						],
					),
				],
			),
			h.div(
				[h.Class("flex flex-col gap-1 text-sm")],
				[
					h.span([], [i18next.t("agents.share.personLabel")]),
					h.submodel({
						slotId: "share-record-picker",
						view: Picker.view,
						model: s.picker,
						toParentMessage: (message: Picker.Message) =>
							Message.GotPickerMessage({ message }),
						viewInputs: {},
					}),
				],
			),
			sharePreview(h),
			h.div(
				[h.Class("flex justify-end")],
				[
					h.button(
						[
							h.Type("submit"),
							h.Class("btn btn-primary btn-sm"),
							h.Disabled(s.creating || s.label.trim() === "" || !agentId),
							h.AriaLabel(i18next.t("agents.share.create")),
						],
						[
							s.creating
								? Loader2(h, { size: 16, class: "animate-spin" })
								: h.empty,
							i18next.t("agents.share.create"),
						],
					),
				],
			),
		],
	);
};

const createdLink = (
	h: H,
	model: Model,
	{ label, url }: { label: string; url: string },
): Html =>
	h.div(
		[
			h.Role("status"),
			h.DataAttribute("testid", "agent-link"),
			h.Class(
				"alert alert-success alert-soft flex flex-col items-stretch gap-2",
			),
		],
		[
			h.p(
				[h.Class("text-sm font-semibold")],
				[i18next.t("agents.share.created", { label })],
			),
			h.div(
				[h.Class("flex gap-2")],
				[
					h.input([
						h.Id("agent-link-value"),
						h.Class("input input-sm grow min-w-0 font-mono"),
						h.Readonly(true),
						h.Value(url),
						h.AriaLabel(i18next.t("agents.share.urlLabel")),
					]),
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-sm"),
							h.OnClick(Message.ClickedCopyLink()),
							h.AriaLabel(i18next.t("agents.share.copy")),
						],
						[
							model.share.copied
								? i18next.t("agents.share.copied")
								: i18next.t("agents.share.copy"),
						],
					),
				],
			),
			h.p([h.Class("text-sm")], [i18next.t("agents.share.once", { label })]),
			h.div(
				[h.Class("flex justify-end")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-ghost btn-sm"),
							h.OnClick(Message.ClickedDoneShare()),
							h.AriaLabel(i18next.t("agents.share.done")),
						],
						[i18next.t("agents.share.done")],
					),
				],
			),
		],
	);

const linkRow = (
	h: H,
	link: AgentLinkSummary,
	name: Name,
	now: number,
	shared: Shared,
): Html => {
	const active = link.status === "active";
	return h.li(
		[
			h.Key(link.id),
			h.DataAttribute("testid", "shared-link"),
			h.Class(
				"flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-base-200 pb-2 text-sm last:border-0",
			),
		],
		[
			h.div(
				[h.Class("min-w-0 flex-1 basis-40")],
				[
					link.forRecordId === null
						? h.p([h.Class("truncate font-semibold")], [link.label])
						: h.a(
								[
									h.Href(recordHref(shared, link.forRecordId)),
									h.AriaLabel(
										i18next.t("agents.share.openRecord", { label: link.label }),
									),
									h.Class("link link-hover truncate font-semibold"),
								],
								[link.label],
							),
					h.p(
						[h.Class("text-xs text-base-content/70")],
						[
							i18next.t("agents.share.via", { agent: name(link.agentId) }),
							" · ",
							i18next.t("agents.share.createdAt", {
								time: relativeTime(link.createdAt, now),
							}),
						],
					),
				],
			),
			h.span(
				[h.Class("text-xs text-base-content/70 whitespace-nowrap")],
				[
					link.lastUsedAt === null
						? i18next.t("agents.share.neverUsed")
						: i18next.t("agents.share.lastUsed", {
								time: relativeTime(link.lastUsedAt, now),
							}),
					" · ",
					i18next.t("agents.share.uses", { count: link.uses }),
				],
			),
			h.span(
				[
					h.Class(
						`badge badge-sm ${active ? "badge-success badge-soft text-base-content" : "badge-ghost"}`,
					),
				],
				[i18next.t(active ? "agents.share.active" : "agents.share.revoked")],
			),
			active && Option.isNone(shared.asOf)
				? smallButton(
						h,
						i18next.t("agents.share.revoke"),
						i18next.t("agents.share.revokeLabel", { label: link.label }),
						Message.ClickedRevokeLink({ id: link.id, label: link.label }),
						"btn-ghost text-error",
					)
				: h.empty,
		],
	);
};

const shareCard = (
	h: H,
	model: Model,
	data: AgentsData,
	shared: Shared,
	name: Name,
): Html =>
	card(h, "share-agent", i18next.t("agents.share.heading"), [
		muted(h, i18next.t("agents.share.help")),
		Option.isSome(shared.asOf)
			? h.empty
			: Option.match(model.share.created, {
					onSome: (created) => createdLink(h, model, created),
					onNone: () =>
						data.keys.length === 0
							? muted(h, i18next.t("agents.share.noAgents"))
							: shareForm(h, model, data),
				}),
		Option.match(model.share.error, {
			onNone: () => h.empty,
			onSome: (error) =>
				h.p([h.Role("alert"), h.Class("text-sm text-error")], [error]),
		}),
		h.h3(
			[h.Class("text-sm font-semibold")],
			[i18next.t("agents.share.listHeading")],
		),
		data.links.length === 0
			? muted(h, i18next.t("agents.share.empty"))
			: h.ul(
					[h.Class("flex flex-col gap-2")],
					data.links.map((link) => linkRow(h, link, name, data.now, shared)),
				),
	]);

// ---- Agents you can reach -------------------------------------------------

const reachableRow = (
	h: H,
	r: ReachableAgent,
	shared: Shared,
	sameName: boolean,
): Html =>
	h.li(
		[
			h.Key(r.recordId),
			h.DataAttribute("testid", "reachable-agent"),
			h.Class("flex flex-wrap items-center gap-2 text-sm"),
		],
		[
			icon(h, "Bot", lucide.Bot, { size: 16, class: "text-secondary" }),
			h.a(
				[
					h.Href(recordHref(shared, r.recordId)),
					h.AriaLabel(
						i18next.t("agents.reachable.openLabel", { name: r.name }),
					),
					h.Class("link link-hover min-w-0 flex-1 truncate font-medium"),
				],
				[r.name || "—"],
			),
			h.span(
				[h.Class("badge badge-sm badge-ghost")],
				[
					i18next.t(
						r.kind === "rhysomn"
							? "agents.reachable.rhysomn"
							: "agents.reachable.external",
					),
				],
			),
			r.live === false
				? h.span(
						[h.Class("badge badge-sm badge-warning")],
						[i18next.t("agents.reachable.dead")],
					)
				: h.empty,
			sameName
				? h.p(
						[
							h.Class(
								"basis-full rounded px-2 py-1 text-xs bg-warning text-warning-content",
							),
						],
						[i18next.t("agents.reachable.sameName")],
					)
				: h.empty,
		],
	);

const nameCount = (rows: readonly ReachableAgent[], name: string): number =>
	rows.filter((r) => r.name === name).length;

/** Someone gave you their agent link: save it on their person record. */
const pasteForm = (h: H, model: Model): Html => {
	const p = model.paste;
	return h.form(
		[
			h.Id("paste-form"),
			h.DataAttribute("testid", "paste-link"),
			h.Class("flex flex-col gap-2 border-t border-base-200 pt-3"),
			h.OnSubmit(Message.SubmittedPaste()),
		],
		[
			h.h3(
				[h.Class("text-sm font-semibold")],
				[i18next.t("agents.paste.heading")],
			),
			h.div(
				[h.Class("flex flex-col gap-1 text-sm")],
				[
					h.span([], [i18next.t("agents.paste.personLabel")]),
					h.submodel({
						slotId: "paste-record-picker",
						view: Picker.view,
						model: p.picker,
						toParentMessage: (message: Picker.Message) =>
							Message.GotPastePickerMessage({ message }),
						viewInputs: {},
					}),
				],
			),
			h.div(
				[h.Class("flex flex-col gap-1 text-sm")],
				[
					h.label([h.For("paste-url")], [i18next.t("agents.paste.urlLabel")]),
					h.input([
						h.Id("paste-url"),
						h.Type("url"),
						h.Class("input input-sm w-full"),
						h.Placeholder(i18next.t("agents.paste.urlPlaceholder")),
						h.Value(p.url),
						h.OnInput((value) => Message.ChangedPasteUrl({ value })),
					]),
				],
			),
			Option.match(p.error, {
				onNone: () => h.empty,
				onSome: (error) =>
					h.p([h.Role("alert"), h.Class("text-sm text-error")], [error]),
			}),
			Option.match(p.saved, {
				onNone: () => h.empty,
				onSome: (name) =>
					h.p(
						[h.Role("status"), h.Class("text-sm font-medium")],
						[i18next.t("agents.paste.saved", { name })],
					),
			}),
			h.div(
				[h.Class("flex justify-end")],
				[
					h.button(
						[
							h.Type("submit"),
							h.Class("btn btn-sm btn-outline"),
							h.Disabled(p.saving || p.url.trim() === ""),
							h.AriaLabel(i18next.t("agents.paste.saveLabel")),
						],
						[
							p.saving
								? Loader2(h, { size: 16, class: "animate-spin" })
								: h.empty,
							i18next.t("agents.paste.save"),
						],
					),
				],
			),
		],
	);
};

const reachableCard = (
	h: H,
	model: Model,
	data: AgentsData,
	shared: Shared,
): Html =>
	card(h, "reachable-agents", i18next.t("agents.reachable.heading"), [
		muted(
			h,
			i18next.t(
				data.keys.length === 0
					? "agents.reachable.helpNoAgents"
					: "agents.reachable.help",
			),
		),
		data.reachable.length === 0
			? muted(h, i18next.t("agents.reachable.empty"))
			: h.ul(
					[h.Class("flex flex-col gap-2")],
					data.reachable.map((r) =>
						reachableRow(h, r, shared, nameCount(data.reachable, r.name) > 1),
					),
				),
		Option.isSome(shared.asOf) ? h.empty : pasteForm(h, model),
	]);

// ---- Waiting for your OK --------------------------------------------------

const pendingRow = (
	h: H,
	p: PendingSend,
	name: Name,
	now: number,
	model: Model,
	shared: Shared,
): Html =>
	h.li(
		[
			h.Key(p.id),
			h.DataAttribute("testid", "pending-send"),
			h.Class(
				"flex flex-col gap-1.5 border-b border-base-200 pb-2 text-sm last:border-0",
			),
		],
		[
			h.p(
				[h.Class("font-semibold")],
				[i18next.t("agents.route", { from: name(p.agentId), to: p.to })],
			),
			h.p(
				[h.Class("text-xs text-base-content/70 break-all")],
				[
					// The heading already names the origin when the send went to a bare URL.
					...(p.to === p.origin ? [] : [p.origin, " · "]),
					i18next.t("agents.approval.expires", {
						time: i18next.t("agents.expiresNext.countdown", {
							duration: durationText(Date.parse(p.expiresAt) - now),
						}),
					}),
				],
			),
			p.preview
				? h.blockquote(
						[
							h.Class(
								"border-l-2 border-base-300 pl-2 text-base-content/80 break-words",
							),
						],
						[p.preview],
					)
				: h.empty,
			Option.isSome(shared.asOf)
				? h.empty
				: h.div(
						[h.Class("flex justify-end gap-1")],
						[
							smallButton(
								h,
								i18next.t("agents.approval.deny"),
								i18next.t("agents.approval.denyLabel", { to: p.to }),
								Message.ClickedDeny({ id: p.id }),
								"btn-ghost",
								model.writing,
							),
							smallButton(
								h,
								i18next.t("agents.approval.approve"),
								i18next.t("agents.approval.approveLabel", { to: p.to }),
								Message.ClickedApprove({ id: p.id }),
								"btn-primary",
								model.writing,
							),
						],
					),
		],
	);

/** The approved send's result: it went out, or why it did not. Stays until dismissed. */
const sendOutcome = (
	h: H,
	{ to, error }: { to: string; error: Option.Option<string> },
): Html =>
	h.div(
		[
			h.Role(Option.isSome(error) ? "alert" : "status"),
			h.DataAttribute("testid", "send-outcome"),
			h.Class(
				`alert alert-soft flex items-start gap-2 text-sm ${Option.isSome(error) ? "alert-error" : "alert-success"}`,
			),
		],
		[
			h.p(
				[h.Class("min-w-0 flex-1 break-words")],
				[
					Option.match(error, {
						onNone: () => i18next.t("agents.approval.sent", { to }),
						onSome: (error) =>
							i18next.t("agents.approval.notSent", { to, error }),
					}),
				],
			),
			smallButton(
				h,
				i18next.t("agents.approval.dismiss"),
				i18next.t("agents.approval.dismissLabel"),
				Message.ClickedDismissOutcome(),
			),
		],
	);

const approvalCard = (
	h: H,
	model: Model,
	data: AgentsData,
	shared: Shared,
	name: Name,
): Html =>
	card(h, "pending-sends", i18next.t("agents.approval.heading"), [
		Option.match(model.sendOutcome, {
			onNone: () => h.empty,
			onSome: (outcome) => sendOutcome(h, outcome),
		}),
		data.pendingSends.length === 0
			? muted(h, i18next.t("agents.approval.empty"))
			: h.ul(
					[h.Class("flex flex-col gap-2")],
					data.pendingSends.map((p) =>
						pendingRow(h, p, name, data.now, model, shared),
					),
				),
	]);

// ---- Page -----------------------------------------------------------------

const content = (
	h: H,
	model: Model,
	data: AgentsData,
	shared: Shared,
): Html[] => {
	const name = namer(data, shared);
	return [
		expiresNext(h, data, name),
		shareCard(h, model, data, shared, name),
		h.div(
			[h.Class("grid gap-4 lg:grid-cols-12 items-start")],
			[
				h.div(
					[h.Class("lg:col-span-4 flex flex-col gap-4 min-w-0")],
					[
						approvalCard(h, model, data, shared, name),
						reachableCard(h, model, data, shared),
						waiting(h, data, name),
					],
				),
				h.div(
					[h.Class("lg:col-span-8 flex flex-col gap-4 min-w-0")],
					[contexts(h, data, shared, name), sent(h, data, name)],
				),
			],
		),
	];
};

const confirm = (h: H, model: Model): Html => {
	const pending = Option.getOrUndefined(model.pending);
	const kind = pending?.kind ?? "revoke";
	const title = pending?.title ?? "";
	return confirmDialog(h, {
		slotId: "confirm-agents",
		model: model.dialog,
		toParentMessage: (message) => Message.GotDialogMessage({ message }),
		title: i18next.t(`agents.confirm.${kind}Title`, { title }),
		message: i18next.t(`agents.confirm.${kind}Message`, { title }),
		confirmLabel: i18next.t(`agents.confirm.${kind}`),
		cancelLabel: i18next.t("agents.confirm.cancel"),
		onConfirm: Message.ClickedConfirm(),
		isPending: model.writing,
	});
};

// ---- Connect an agent -----------------------------------------------------

/** One form makes an agent: its API key is the agent's identity (shown once). */
const connectCard = (h: H, model: Model): Html => {
	const c = model.connect;
	const mcpUrl = `${imageBase()}/mcp`;
	return card(h, "connect-agent", i18next.t("agents.connect.heading"), [
		muted(h, i18next.t("agents.connect.help")),
		Option.match(c.created, {
			onNone: () =>
				h.form(
					[
						h.Id("connect-form"),
						h.Class("flex flex-wrap items-end gap-2"),
						h.OnSubmit(Message.SubmittedConnect()),
					],
					[
						h.label(
							[
								h.For("agent-name"),
								h.Class("flex grow flex-col gap-1 text-sm"),
							],
							[
								i18next.t("agents.connect.nameLabel"),
								h.input([
									h.Id("agent-name"),
									h.Class("input input-sm w-full"),
									h.Placeholder(i18next.t("agents.connect.namePlaceholder")),
									h.Value(c.name),
									h.OnInput((value) => Message.ChangedAgentName({ value })),
								]),
							],
						),
						h.button(
							[
								h.Type("submit"),
								h.Class("btn btn-primary btn-sm"),
								h.Disabled(c.creating || c.name.trim() === ""),
								h.AriaLabel(i18next.t("agents.connect.create")),
							],
							[
								c.creating
									? Loader2(h, { size: 16, class: "animate-spin" })
									: h.empty,
								i18next.t("agents.connect.create"),
							],
						),
					],
				),
			onSome: ({ name, key }) =>
				h.div(
					[
						h.Role("status"),
						h.DataAttribute("testid", "agent-key"),
						h.Class(
							"alert alert-success alert-soft flex flex-col items-stretch gap-2",
						),
					],
					[
						h.p(
							[h.Class("text-sm font-semibold")],
							[i18next.t("agents.connect.created", { name })],
						),
						h.div(
							[h.Class("flex gap-2")],
							[
								h.input([
									h.Id("agent-key-value"),
									h.Class("input input-sm grow font-mono"),
									h.Readonly(true),
									h.Value(key),
									h.AriaLabel(i18next.t("agents.connect.keyLabel")),
								]),
								h.button(
									[
										h.Type("button"),
										h.Class("btn btn-sm"),
										h.OnClick(Message.ClickedCopyKey()),
										h.AriaLabel(i18next.t("agents.connect.copy")),
									],
									[
										c.copied
											? i18next.t("agents.connect.copied")
											: i18next.t("agents.connect.copy"),
									],
								),
							],
						),
						h.p([h.Class("text-sm")], [i18next.t("agents.connect.once")]),
						h.p(
							[h.Class("text-sm")],
							[i18next.t("agents.connect.setup", { url: mcpUrl })],
						),
						h.pre(
							[h.Class("overflow-x-auto rounded bg-base-200 p-2 text-xs")],
							[
								`claude mcp add --transport http rhysomn ${mcpUrl} --header "Authorization: Bearer ${key}"`,
							],
						),
						h.div(
							[h.Class("flex justify-end")],
							[
								h.button(
									[
										h.Type("button"),
										h.Class("btn btn-ghost btn-sm"),
										h.OnClick(Message.ClickedDoneConnect()),
										h.AriaLabel(i18next.t("agents.connect.done")),
									],
									[i18next.t("agents.connect.done")],
								),
							],
						),
					],
				),
		}),
		Option.match(c.error, {
			onNone: () => h.empty,
			onSome: (error) =>
				h.p([h.Role("alert"), h.Class("text-sm text-error")], [error]),
		}),
	]);
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const data = AsyncData.getOrElse(model.data, () => null);
	return h.div(
		[
			h.DataAttribute("testid", "page-Agents"),
			h.Class("mx-auto flex max-w-7xl flex-col gap-4"),
		],
		[
			h.header(
				[h.Class("flex flex-wrap items-baseline gap-x-4")],
				[
					h.h1([h.Class("text-2xl font-bold")], [i18next.t("agents.title")]),
					h.p(
						[h.Class("text-sm text-base-content/70")],
						[i18next.t("agents.subtitle")],
					),
				],
			),
			Option.isSome(shared.asOf) ? h.empty : connectCard(h, model),
			Option.isSome(shared.asOf)
				? h.p(
						[
							h.Role("status"),
							h.DataAttribute("testid", "live-notice"),
							h.Class("alert alert-info alert-soft py-2 text-sm"),
						],
						[i18next.t("agents.liveNotice")],
					)
				: h.empty,
			Option.match(model.error, {
				onNone: () => h.empty,
				onSome: (error) =>
					h.div(
						[h.Role("alert"), h.Class("alert alert-error py-2 text-sm")],
						[h.span([], [error])],
					),
			}),
			...(data === null
				? [
						AsyncData.isFailure(model.data)
							? h.div(
									[h.Role("alert"), h.Class("alert alert-error")],
									[h.span([], [i18next.t("agents.loadError")])],
								)
							: h.div(
									[
										h.Role("status"),
										h.Class(
											"flex items-center gap-2 py-8 text-base-content/70",
										),
									],
									[
										Loader2(h, { size: 20, class: "animate-spin" }),
										h.span([], [i18next.t("agents.loading")]),
									],
								),
					]
				: content(h, model, data, shared)),
			confirm(h, model),
		],
	);
});
